import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { WarningsService } from 'app/data-studio-redesign/services/warnings.service';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { DataStudioGranularityType } from 'app/shared/services/data-services/graphql';
import { map, takeUntil } from 'rxjs/operators';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import {SignalSelectionCacher} from '../signal-selection-cacher/signal-selection-cacher';
import { CurrencyService } from 'app/shared/services/currency-service/currency.service';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { addSignal, addSignalWithTurbineType } from 'app/data-studio-redesign/helper/data-studio-signal.helper';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { defineLocale} from 'ngx-bootstrap/chronos';
import * as locale from 'ngx-bootstrap/locale';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
    selector: 'app-data-park',
    templateUrl: './data-park.component.html',
    styleUrls: ['./data-park.component.scss'],
    standalone: false
})
export class DataParkComponent implements OnInit, OnChanges, OnDestroy {

  public bsConfig;
  public bsRangeValue: Date[];
  public minDate = new Date('01-01-2019');
  public maxDate = new Date();
  public theme$: Observable<any>;
  public selectedAggregation = 'park';
  public dataSelectOptions: any = {};
  public dataParks: Array<any>;
  public parkSelected;
  public signalGroupArray: Array<any> = [];
  public totalSignals = 0;
  public totalSignalsSelected = 0;
  public selectAllSignalsFlag = false;
  public parkProductionSignalGroupSelected = 0;
  public enumGranularity = DataStudioGranularityType;
  private unsubscribe$ = new Subject();
  public wrongDate = false;
  public marketPriceSignalGroupSelected = 0;
  public dataParksNames: string[];
  private unsubscriber = new ReplaySubject();

  constructor(private dataStudioService: DataStudioRedesignService,
              private warningService: WarningsService,
              private themeService: ThemeService,
              private currencyService: CurrencyService, 
              private dataStudioHelper: DataStudioHelperService,
              private selectedSignalsCacher: SignalSelectionCacher,
              private translateService: TranslateService,
              private localeService: BsLocaleService,       
              private languageService: LanguageService,       
							private dateTimeFormatService: DateTimeFormatService) {
              this.bsRangeValue = [new Date(), new Date()];
              this.initSelectOptions();
  }

  ngOnInit(): void {
    this.subscribeLanguageChange();
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, { containerClass: theme, rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(), dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase() }))
    );
    this.getAllParkAndTurbines();
  }

	ngOnChanges(changes): void {
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, { containerClass: theme, rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(), dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase() }))
    );
	}

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  public changeDateRange(event) {
    if (event) {
      const dateA = new Date(event[0].getTime());
      this.dataSelectOptions.dateFrom = Number(dateA.setHours(0, 0, 0, 0));
      this.dataSelectOptions.fileRequest.startTS = this.dataSelectOptions.dateFrom;
      const dateB = new Date(event[1].getTime());
      dateB.setDate(dateB.getDate() + 1)
      this.dataSelectOptions.dateTo = Number(dateB.setHours(0, 0, 0, 0));
      this.dataSelectOptions.fileRequest.endTS = this.dataSelectOptions.dateTo;
      if (this.dataSelectOptions.fileRequest.signals !== undefined) {
        this.calculateSize();
      }
    }
  }

  public selectPark(event = null) {
    this.parkSelected = event ? this.dataParks[event.index] : this.dataParks[0];
    this.getAvailableTSForPark(this.parkSelected.name);
    this.getSignalsByParkName(this.parkSelected);
    this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare);
    this.dataSelectOptions.assetPark = this.parkSelected.code;
    this.dataSelectOptions.fileRequest.park = this.parkSelected.name;
    this.calculateSize();
  }

  private getSignalsByParkName(parkName: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
    this.checkSelectedSignals();
  }

  private resetSignalArray() {
    this.dataSelectOptions.fileRequest.signals = [];
    this.dataSelectOptions.fileRequest.turbines = [];
    this.totalSignalsSelected = 0;
    this.selectAllSignalsFlag = false;
    this.totalSignals = this.parkProductionSignalGroupSelected = 0;
    this.signalGroupArray = [];
    const currency = this.currencyService.getCurrencySymbol();
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'DATA_STUDIO.SIGNALS.GROUPS.AMBIENT'}, signals: [
      { checked: false, name: 'SIGNALS.AVG_TEMPERATURE.DISPLAY_NAME', value: 'temperature', unit: '°C', description: '' },
      { checked: false, name: 'SIGNALS.AVG_WIND_SPEED.DISPLAY_NAME', value: 'windSpeed', unit: 'm/s', description: '' },
    ]});
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'DATA_STUDIO.SIGNALS.GROUPS.PARK_PRODUCTION'}, signals: [
        { checked: false, name: 'SIGNALS.PRODUCTION_SCADA.DISPLAY_NAME', value: 'productionScada', unit: 'MWh', description: '' },
        { checked: false, name: 'SIGNALS.PRODUCTION_GRID.DISPLAY_NAME', value: 'productionGrid', unit: 'MWh', description: '' },
        { checked: false, name: 'SIGNALS.THEORETICAL_PRODUCTION.DISPLAY_NAME', value: 'theoreticalProduction', unit: 'MWh', description: '' },
        { checked: false, name: 'SIGNALS.ELECTRICITY.DISPLAY_NAME', value: 'electricityLosses', unit: '%', description: '' },
        { checked: false, name: 'SIGNALS.ENERGY_CONSUMPTION.DISPLAY_NAME', value: 'consumptionGrid', unit: 'MWh', description: '' },
        { checked: false, name: 'SIGNALS.REV_ELEC.DISPLAY_NAME', value: 'electricitySalesRevenue', unit: currency, description: '' },
        { checked: false, name: 'SIGNALS.REV_INC.DISPLAY_NAME', value: 'incentiveRevenue', unit: currency, description: '' },
        { checked: false, name: 'SIGNALS.TOTAL_REV.DISPLAY_NAME', value: 'totalRevenue', unit: currency, description: '' },
        { checked: false, name: 'SIGNALS.ENERGY_CONSUMP.DISPLAY_NAME', value: 'consumptionLosses', unit: currency, description: '' },
    ]});
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'CHECK_REVENUES.OVERVIEW.REVENUE_CHART.LABELS.MARKET_PRICE'}, signals: [
        { checked: false, name: 'SIGNALS.AVG_MARKET_ELEC.DISPLAY_NAME', value: 'marketPrice', unit: currency + '/MWh', description: '' },
        { checked: false, name: 'SIGNALS.AVG_PPA_ELEC.DISPLAY_NAME', value: 'PPAElectricityPrice', unit: currency + '/MWh', description: '' },
    ]});
    this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
    this.dataStudioService.sendSignalsToPreview(this.signalGroupArray.flatMap(e => e.signals));
    this.totalSignals = this.signalGroupArray.flatMap(e => e.signals).length;
  }

  private getAllParkAndTurbines() {
    this.dataStudioService.getAllParkAndTurbines().subscribe(response => {
      if (response) {
        this.dataParks = JSON.parse(JSON.stringify(response.filter(p => p.energyMeterAvailable)));
        this.dataParks.sort((a, b) => {
          return a.fe_name.localeCompare(b.fe_name);
        });
        this.dataParksNames = this.dataParks.map(park => park.fe_name);
        this.dataStudioService.sendTurbinesToPreview(this.dataParks);
        this.initSelectOptions();
        this.selectPark();
      }
    });
  }

  public selectAllSignals() {
    this.selectAllSignalsFlag = !this.selectAllSignalsFlag;
    this.signalGroupArray.forEach(element => {
      element.signalGroupName.checked = this.selectAllSignalsFlag;
      element.signals.forEach(signal => {
        signal.checked = this.selectAllSignalsFlag;
      });
    });
    this.checkSelectedSignals();
  }

  public calculateSize() {
    const days = this.warningService.getDays(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
    const columns = 5 + (this.dataSelectOptions.fileRequest.signals.length);  // Right now 5 fields: Date UTC, Turbine Timezone date, User Timezone date, Turbine Code, Turbine Serial
    let rows = this.warningService.getRowsNumber(this.dataSelectOptions.granularity, days);
    let fields = this.warningService.getFieldsNumber(this.dataSelectOptions.granularity, columns, days);
    if (this.dataSelectOptions.aggregationLevel === '0' || this.dataSelectOptions.aggregationLevel === '2') { // ParkLevel  -> we multiply rows & fields for the number of turbines before getting the file size
        let turbines = 0;
        this.dataSelectOptions.fileRequest.turbines.forEach(element => {
          turbines++;
        });
        if (turbines > 0) {
          rows = rows * turbines;
          fields = fields * turbines;
        }
        this.dataSelectOptions.estimatedRows = rows;
        this.dataSelectOptions.estimatedFields = fields;
        this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    } else {
      this.dataSelectOptions.estimatedRows = rows;
      this.dataSelectOptions.estimatedFields = fields;
      this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    }
    this.dataStudioHelper.setDateObservable$(this.dataSelectOptions);
  }

  private initSelectOptions() {
    this.dataSelectOptions.aggregationLevels = ['park'];
    this.dataSelectOptions.aggregationLevel = '0';
    this.dataSelectOptions.assetSelected = null;
    this.dataSelectOptions.assetPark = null;
    this.dataSelectOptions.dateFrom = new Date().setHours(0, 0, 0, 0);
    this.dataSelectOptions.dateTo = new Date().getTime();
    this.dataSelectOptions.granularities = [ '1 hour'];
    this.dataSelectOptions.granularity = 1;
    this.dataSelectOptions.selectableColumns = [];
    this.dataSelectOptions.selectableColumnsNames = [];
    this.dataSelectOptions.selectableUnits = [];
    this.dataSelectOptions.selectedColumns = [];
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.dataSelectOptions.fileRequest = {
      fileName: '',
      turbines: [],
      signals: [],
      granularity: this.enumGranularity.G1H,
      queryType: 'parkdata',
      fileType: '',
      startTS: Number(this.dataSelectOptions.dateFrom),
      endTS: Number(this.dataSelectOptions.dateTo)
    };
  }

  public selectAllSignalsByGroup(groupName) {
    this.signalGroupArray.forEach(element => {
      if (element.signalGroupName.name === groupName) {
        element.signals.forEach(signal => {
          signal.checked = element.signalGroupName.checked;
        });
      }
    });
    this.checkSelectedSignals();
  }

  public checkSelectedSignals(): void {
    this.dataSelectOptions.fileRequest.signals = [];
    let signals = 0;
    this.signalGroupArray.forEach(element => {
      element.signals.forEach(signal => {
        if (signal.checked) {
          signals++;
          if (!this.dataSelectOptions.fileRequest.signals.includes(signal.value)) {
            this.dataSelectOptions.fileRequest.signals.push(signal.value);
          }
        }
      });
      switch (element.signalGroupName.name) {
        case 'DATA_STUDIO.SIGNALS.GROUPS.PARK_PRODUCTION':
          const parkProductionSignalsSelected = element.signals.filter(data => data.checked === true);
          this.parkProductionSignalGroupSelected = parkProductionSignalsSelected.length;
          element.signalGroupName.checked = element.signals.length === this.parkProductionSignalGroupSelected ? true : false;
          break;
        case 'CHECK_REVENUES.OVERVIEW.REVENUE_CHART.LABELS.MARKET_PRICE':
          const marketPriceSignalsSelected = element.signals.filter(data => data.checked === true);
          this.marketPriceSignalGroupSelected = marketPriceSignalsSelected.length;
          element.signalGroupName.checked = element.signals.length === this.marketPriceSignalGroupSelected ? true : false;
          break;
      }
    });
    this.totalSignalsSelected = signals;
    this.selectAllSignalsFlag = this.totalSignalsSelected === this.totalSignals ? true : false;
    this.calculateSize();
  }

  private getAvailableTSForPark(parkName) {
    this.dataStudioService.getAvailableTS(parkName).pipe(takeUntil(this.unsubscribe$)).subscribe(data => {
      if (data) {
        this.minDate = new Date(data);
      }
    });
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
        this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
      }
    });
  }

  private sortTranslateSignals(signalGroupArray){
    return signalGroupArray.map(item => ({
      ...item,
      signals: item.signals.sort((a,b) => {
        const translatedA = this.translateService.instant(a.name) || a.name;
        const translatedB = this.translateService.instant(b.name) || b.name;
        return translatedA.localeCompare(translatedB);
      })
    }))
  }

}

